import { useMemo, useState, memo } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { formatRelativeTime } from '@/lib/formatRelativeTime';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LoginArea } from '@/components/auth/LoginArea';
import type { NostrEvent } from '@nostrify/nostrify';
import { useDesktop } from '@/desktop/DesktopContext';
import {
  AtSign,
  Heart,
  Repeat2,
  Zap,
  Bell,
  RefreshCw,
} from 'lucide-react';

type NotifTab = 'all' | 'mentions' | 'reactions' | 'reposts' | 'zaps';

function notifIcon(kind: number) {
  if (kind === 1) return <AtSign className="w-3.5 h-3.5 text-blue-500" />;
  if (kind === 6) return <Repeat2 className="w-3.5 h-3.5 text-green-500" />;
  if (kind === 7) return <Heart className="w-3.5 h-3.5 text-rose-500" />;
  if (kind === 9735) return <Zap className="w-3.5 h-3.5 text-amber-500" />;
  return <Bell className="w-3.5 h-3.5 text-muted-foreground" />;
}

function notifLabel(kind: number): string {
  if (kind === 1) return 'mentioned you';
  if (kind === 6) return 'reposted you';
  if (kind === 7) return 'reacted';
  if (kind === 9735) return 'zapped you';
  return 'interacted';
}

const NotifCard = memo(function NotifCard({
  event,
  onOpen,
  onOpenProfile,
}: {
  event: NostrEvent;
  onOpen: (e: NostrEvent) => void;
  onOpenProfile: (pubkey: string) => void;
}) {
  // Determine the target event to open in the feed (the referenced post)
  const targetEventId = event.tags.find(([t]) => t === 'e')?.[1];
  const isDirectMention = event.kind === 1 && !targetEventId;

  const handleClick = () => {
    if (isDirectMention) {
      onOpen(event);
    } else if (targetEventId) {
      // For reactions/reposts/zaps/replies we want to open the original post
      // Create a minimal event shell so ThreadView can load it
      onOpen({ id: targetEventId, kind: 1, pubkey: '', created_at: 0, content: '', tags: [], sig: '' } as NostrEvent);
    } else {
      onOpen(event);
    }
  };

  // For zaps, the sender is in the description tag of the zap receipt
  const senderPubkey = event.kind === 9735
    ? (() => {
        try {
          const desc = event.tags.find(([t]) => t === 'description')?.[1];
          if (desc) return (JSON.parse(desc) as { pubkey: string }).pubkey;
        } catch { /* fall through */ }
        return event.pubkey;
      })()
    : event.pubkey;

  const author = useAuthor(senderPubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? metadata?.display_name ?? genUserName(senderPubkey);

  // For reactions, show the reaction character
  const reactionContent = event.kind === 7
    ? (event.content === '+' ? '👍' : event.content === '-' ? '👎' : event.content || '❤️')
    : null;

  // For zaps, show the amount
  const zapAmount = event.kind === 9735
    ? (() => {
        try {
          const bolt11 = event.tags.find(([t]) => t === 'bolt11')?.[1];
          if (!bolt11) return null;
          // Quick decode: extract amount from bolt11 (millisats in the invoice)
          const match = bolt11.match(/lnbc(\d+)([munp])/i);
          if (!match) return null;
          const num = parseInt(match[1]);
          const unit = match[2].toLowerCase();
          const msats = unit === 'm' ? num * 100000000
            : unit === 'u' ? num * 100000
            : unit === 'n' ? num * 100
            : num * 100; // pico
          return Math.round(msats / 1000);
        } catch { return null; }
      })()
    : null;

  // Preview of the content / referenced note
  const preview = event.kind === 1
    ? event.content.slice(0, 120)
    : event.kind === 6
    ? 'Reposted your note'
    : null;

  return (
    <div
      className="px-3 py-2.5 border-b border-border/40 hover:bg-muted/20 transition-colors cursor-pointer [content-visibility:auto] [contain-intrinsic-size:auto_56px]"
      onClick={handleClick}
    >
      <div className="flex gap-2.5">
        {/* Type icon */}
        <div className="w-5 flex flex-col items-center pt-1 shrink-0 gap-1">
          {notifIcon(event.kind)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <button className="shrink-0" onClick={(e) => { e.stopPropagation(); onOpenProfile(senderPubkey); }}>
              <Avatar className="w-6 h-6">
                <AvatarImage src={metadata?.picture} alt={displayName} />
                <AvatarFallback className="text-[8px]">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
            </button>
            <button className="text-xs font-semibold hover:underline truncate max-w-[120px]" onClick={(e) => { e.stopPropagation(); onOpenProfile(senderPubkey); }}>
              {displayName}
            </button>
            <span className="text-[10px] text-muted-foreground">{notifLabel(event.kind)}</span>
            {reactionContent && (
              <span className="text-sm leading-none">{reactionContent}</span>
            )}
            {zapAmount !== null && (
              <Badge variant="outline" className="h-4 text-[10px] px-1 gap-0.5 text-amber-600 border-amber-300">
                <Zap className="w-2.5 h-2.5" />
                {zapAmount.toLocaleString()} sats
              </Badge>
            )}
            <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
              {formatRelativeTime(event.created_at)}
            </span>
          </div>
          {preview && (
            <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">{preview}</p>
          )}
        </div>
      </div>
    </div>
  );
});

function NotifSkeleton() {
  return (
    <div className="space-y-0">
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="px-3 py-2.5 border-b border-border/40">
          <div className="flex gap-2.5">
            <Skeleton className="w-5 h-5 rounded shrink-0" />
            <div className="flex-1 space-y-1.5">
              <div className="flex items-center gap-2">
                <Skeleton className="w-6 h-6 rounded-full" />
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-16" />
              </div>
              <Skeleton className="h-3 w-3/4" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const TAB_CONFIG: { id: NotifTab; label: string; icon: React.ReactNode; kinds: number[] }[] = [
  { id: 'all', label: 'All', icon: <Bell className="w-3 h-3" />, kinds: [1, 6, 7, 9735] },
  { id: 'mentions', label: 'Mentions', icon: <AtSign className="w-3 h-3" />, kinds: [1] },
  { id: 'reactions', label: 'Reactions', icon: <Heart className="w-3 h-3" />, kinds: [7] },
  { id: 'reposts', label: 'Reposts', icon: <Repeat2 className="w-3 h-3" />, kinds: [6] },
  { id: 'zaps', label: 'Zaps', icon: <Zap className="w-3 h-3" />, kinds: [9735] },
];

export function NotificationsApp() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { openThread, openProfile } = useDesktop();
  const [tab, setTab] = useState<NotifTab>('all');

  const { data: events, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['notifications', user?.pubkey],
    queryFn: async ({ signal }) => {
      if (!user) return [];
      // Single combined query for all notification kinds
      const results = await nostr.query(
        [
          // mentions + replies (kind 1 that tags the user)
          { kinds: [1], '#p': [user.pubkey], limit: 100 },
          // reposts of user's notes
          { kinds: [6], '#p': [user.pubkey], limit: 50 },
          // reactions on user's notes
          { kinds: [7], '#p': [user.pubkey], limit: 100 },
          // zap receipts
          { kinds: [9735], '#p': [user.pubkey], limit: 50 },
        ],
        { signal }
      );
      // Exclude own events, dedupe, sort newest first
      const seen = new Set<string>();
      return results
        .filter((e) => {
          if (e.pubkey === user.pubkey && e.kind !== 9735) return false;
          if (seen.has(e.id)) return false;
          seen.add(e.id);
          return true;
        })
        .sort((a, b) => b.created_at - a.created_at);
    },
    enabled: !!user,
    staleTime: 30000,
    refetchInterval: 60000,
  });

  // Single pass over the events for all tab badges (was one filter per tab
  // per render).
  const tabCounts = useMemo(() => {
    const counts: Record<NotifTab, number> = { all: 0, mentions: 0, reactions: 0, reposts: 0, zaps: 0 };
    for (const e of events ?? []) {
      counts.all++;
      if (e.kind === 1) counts.mentions++;
      else if (e.kind === 7) counts.reactions++;
      else if (e.kind === 6) counts.reposts++;
      else if (e.kind === 9735) counts.zaps++;
    }
    return counts;
  }, [events]);

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
        <Bell className="w-10 h-10 opacity-30" />
        <p className="text-sm text-muted-foreground">Log in to see your notifications.</p>
        <LoginArea />
      </div>
    );
  }

  const activeKinds = TAB_CONFIG.find((t) => t.id === tab)?.kinds ?? [1, 6, 7, 9735];
  const filtered = (events ?? []).filter((e) => activeKinds.includes(e.kind));

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 py-2 border-b border-border/50 bg-muted/30 shrink-0 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Notifications</h2>
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`w-3 h-3 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 px-2 py-1.5 border-b border-border/40 bg-muted/10 overflow-x-auto shrink-0">
        {TAB_CONFIG.map((t) => {
          const count = tabCounts[t.id];
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors shrink-0 ${
                tab === t.id
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.icon}
              {t.label}
              {count > 0 && (
                <span className={`text-[9px] px-1 rounded-full leading-4 min-w-[16px] text-center ${
                  tab === t.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Feed */}
      <div className="flex-1 overflow-auto min-h-0">
        {isLoading ? (
          <NotifSkeleton />
        ) : filtered.length > 0 ? (
          filtered.map((event) => <NotifCard key={event.id} event={event} onOpen={openThread} onOpenProfile={openProfile} />)
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
            <Bell className="w-8 h-8 opacity-30" />
            <p className="text-sm">No {tab === 'all' ? '' : tab} yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}
